import styles from './customer-product-list-grid-renderer.module.scss';
import { useState } from 'react';
import { GetCurrentCompany } from '../../customer-context/customer-context';
import { useGetRequestNewHook } from '../../requestUtils';
import { UseSignalRHook } from '../../signalr-context/signalr-context';
import { booleanHeader, Box, CreateHeaders, dateAndTimeHeader, dateFormatter, dateHeader, Divider, getISODateString, HeaderSizeEnum, Icon, IWebkuaSignalRResponse, LicenseTypeEnum, OrderTypesEnum, PrimaryButton, Tab, Tabs, textHeader } from '@frontend-workspace/react-shared-lib';
import LicenseDialog from '../license-dialog/license-dialog';
import DomainDialog from '../domain-dialog/domain-dialog';
import CopyLicenseAccessDialog from '../../other/copy-license-access-dialog/copy-license-access-dialog';
import ExternalAccessDialog from '../external-access-dialog/external-access-dialog';
import { ILicenseLoan } from '../../_models/license-loan';
import { ICustomerProducts } from '../../_models/customer-products';
import { IPersonsOnLicense } from '../../_models/persons-on-license';
import { ILicenseInfoCompany } from '../../_models/license-info-company';
import WebkuaTable from '../../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';
import { ValueFormatterParams } from 'ag-grid-community';

/* eslint-disable-next-line */
export interface CustomerProductListGridRendererProps {
  data: ICustomerProducts
}

export function CustomerProductListGridRenderer(props: CustomerProductListGridRendererProps) {
  const { t } = useTranslation();

  if (props.data.hasLicensing) {
    return (
      <CustomerProductListGridRendererContent data={props.data} />
    );
  }
  return (
    <div className="d-flex flex-column py-4 px-2 pb-3">
      <Box sx={{ width: '100%', typography: 'body4' }} >
        <div className='mx-3 my-2'>
          {t('customerProductListGrid.licenseTypeDoesNotHaveLicensing')}
        </div>
      </Box>
    </div>
  );
}

export function CustomerProductListGridRendererContent(props: CustomerProductListGridRendererProps) {
  const { t } = useTranslation();
  const currentCompany = GetCurrentCompany();
  const companyNumber = currentCompany ? currentCompany.number : "";
  const [selectedCompanyNumber, setSelectedCompanyNumber] = useState<string>(companyNumber);

  const { data: licenseAccesses, getData: getLicenseAccesses }: { data: IPersonsOnLicense[], getData: () => void } = useGetRequestNewHook(`companies/${companyNumber}/licenses/${props.data.licenseNumber}/action/getLicenseAccesses`, []);
  const { data: licenseAccessChanges, getData: getLicenseAccessChanges }: { data: ILicenseInfoCompany[], getData: () => void } = useGetRequestNewHook(`Licenses/${props.data.licenseNumber}/action/getLicenseAccessChanges`, []);
  const { data: licenseLoan, isLoading: isLoadingLoans }: { data: ILicenseLoan[], isLoading: boolean } = useGetRequestNewHook(`licenses/${props.data.licenseNumber}/loans/showAll/false`, [], [currentCompany]);

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [licenseAccessOpen, setLicenseAccessOpen] = useState<boolean>(false);
  const [domain, setDomain] = useState<boolean>(false);
  const [copyLicenseAccess, setCopyLicenseAccess] = useState<boolean>(false);
  const [externalAccess, setExternalAccess] = useState<boolean>(false);

  const handleAccessChange = (response: IWebkuaSignalRResponse) => {
    getLicenseAccessChanges();
    getLicenseAccesses();
  };

  UseSignalRHook(handleAccessChange, [OrderTypesEnum.ChangeLicenseAccess, OrderTypesEnum.LicenseAccessChangeOrder]);

  const openLicenseDialog = () => setLicenseAccessOpen(true);
  const closeLicenseDialog = () => setLicenseAccessOpen(false);
  const openDomainDialog = () => setDomain(true);
  const closeDomainDialog = () => setDomain(false);
  const openCopyLicenseAccessDialog = () => setCopyLicenseAccess(true);
  const closeCopyLicenseAccessDialog = () => setCopyLicenseAccess(false);
  const openExternalAccessDialog = () => setExternalAccess(true);
  const closeExternalAccessDialog = () => setExternalAccess(false);
  const handleSelectedCompanyNumber = (c: string) => setSelectedCompanyNumber(c);

  const navigateToTabOne = () => setSelectedTab(0);
  const navigateToTabTwo = () => setSelectedTab(1);
  const navigateToTabThree = () => setSelectedTab(2);

  const userSummary = (header: any, data: any[]) => {
    const field = header.field;
    const uniqueUsers = data.reduce((startValue, d) => {
      if (!startValue.includes(d[field])) {
        startValue.push(d[field]);
      }
      return startValue;
    }, []);
    return `Unike brukere: ${uniqueUsers.length}`;
  };
  const getLastLoanDates = (licenseLoan: any[]) => {
    const lastLoanDates = new Map<string, Date>();

    licenseLoan.forEach((loan) => {
      const currentLastDate = lastLoanDates.get(loan.userName) || new Date(0);
      const loanDate = new Date(loan.loanReturnedTime);
      if (loanDate > currentLastDate) {
        lastLoanDates.set(loan.userName, loanDate);
      }
    });

    return lastLoanDates;
  };

  const lastLoanDatesMap = getLastLoanDates(licenseLoan);

  const lastUsedRenderer = (params: ValueFormatterParams) => {
    if (isLoadingLoans) return t('standard.gettingData');
    const lastLoanDate = lastLoanDatesMap.get(params.data.email) || new Date(0);

    return lastLoanDate.getTime() === new Date(0).getTime() ? "" : dateFormatter(lastLoanDate);
  };
  const lastUsedComparator = (email1: string, email2: string) => {
    if (isLoadingLoans) return 0;

    const lastLoanDate1 = lastLoanDatesMap.get(email1) || new Date(0);
    const lastLoanDate2 = lastLoanDatesMap.get(email2) || new Date(0);

    return lastLoanDate1.getTime() - lastLoanDate2.getTime();
  };


  return <>
    <Box className="d-flex flex-column py-4 px-2">
      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}>
        <Tabs
          value={selectedTab}
          variant="scrollable"
          scrollButtons="auto">
          <Tab label={t('standard.access')} onClick={navigateToTabOne} />
          <Tab label={t('customerProductListGrid.accessHistory')} onClick={navigateToTabTwo} />
          <Tab label={t('standard.loan')} onClick={navigateToTabThree} />
        </Tabs>
      </Box>
      {selectedTab === 0 && <>
        <div className="d-flex flex-row align-items-center flex-wrap ms-2 mb-2 mb-xxl-0">
          <div className='me-4 py-2'>
            <PrimaryButton
              variant="contained"
              onClick={openLicenseDialog}>
              <Icon className="me-1" fontSize="small">add</Icon>{t('standard.assignLicenseRights')}
            </PrimaryButton>
          </div>
          <div className='me-4 py-2'>
            <PrimaryButton
              variant="outlined"
              onClick={openDomainDialog}>
              {t('standard.automaticLicenseApproval')}
            </PrimaryButton>
          </div>
          <div className='me-3 me-sm-0 py-2'>
            <PrimaryButton
              variant="outlined"
              onClick={openCopyLicenseAccessDialog}>
              {t('standard.copyLicenseRights')}
            </PrimaryButton>
          </div>
          <Divider orientation="vertical" variant="middle" flexItem className="mx-4 d-none d-sm-block" />
          <div className='me-4 py-2'>
            <PrimaryButton
              variant="outlined"
              onClick={openExternalAccessDialog}>
              {t('standard.externalLicenseRights')}
            </PrimaryButton>
          </div>
        </div>
        <WebkuaTable
          headers={CreateHeaders([
            textHeader("firstName", t('standard.firstName'), HeaderSizeEnum.LARGE),
            textHeader("lastName", t('standard.lastName'), HeaderSizeEnum.LARGE),
            textHeader("email", t('standard.username'), HeaderSizeEnum.EXTRA_LARGE),
            textHeader("companyNumber", t('standard.companyNo'), HeaderSizeEnum.MEDIUM),
            booleanHeader("isExternal", t('standard.external')),
            booleanHeader("hasAccess", t('standard.license')),
            booleanHeader("isCoordinator", t('standard.licensingCoordinator')),
            textHeader("email", t('standard.lastUsed'), HeaderSizeEnum.SMALL, { renderer: lastUsedRenderer, comparator: lastUsedComparator })
          ])}
          data={licenseAccesses}
        />
      </>}
      {selectedTab === 1 && <>
        <div className="px-2 d-flex flex-row align-items-center flex-wrap">
          <div className='me-4 my-2'>
            <PrimaryButton
              variant="contained"
              onClick={openLicenseDialog}>
              <Icon className="me-1" fontSize="small">add</Icon>{t('standard.assignLicenseRights')}
            </PrimaryButton>
          </div>
          <div className='me-4 my-2'>
            <PrimaryButton
              variant="outlined"
              onClick={openDomainDialog}>
              {t('standard.automaticLicenseApproval')}
            </PrimaryButton>
          </div>
          <div className='me-3 me-sm-0 my-2'>
            <PrimaryButton
              variant="outlined"
              onClick={openCopyLicenseAccessDialog}>
              {t('standard.copyLicenseRights')}
            </PrimaryButton>
          </div>
          <Divider orientation="vertical" variant="middle" flexItem className="mx-4 d-none d-sm-block" />
          <div className='me-4 my-2'>
            <PrimaryButton
              variant="outlined"
              onClick={openExternalAccessDialog}>
              {t('standard.externalLicenseRights')}
            </PrimaryButton>
          </div>
        </div>
        <div className="px-2">
          <WebkuaTable
            headers={CreateHeaders([
              textHeader("firstName", t('standard.firstName'), HeaderSizeEnum.LARGE),
              textHeader("lastName", t('standard.lastName'), HeaderSizeEnum.LARGE),
              textHeader("email", t('standard.username'), HeaderSizeEnum.EXTRA_LARGE),
              dateHeader("activationDate", t('standard.date')),
              booleanHeader("givenAccess", t('customerProductListGridRenderer.givenAccess')),
              booleanHeader("removedAccess", t('customerProductListGridRenderer.removedAccess')),
              booleanHeader("givenCoordinatorRights", t('customerProductListGridRenderer.givenCoordinator')),
              booleanHeader("removedCoordinatorRights", t('customerProductListGridRenderer.removedCoordinator'))
            ])}
            data={licenseAccessChanges}
          />
        </div>
      </>}
      {selectedTab === 2 && <>
        <div className="px-2 d-flex flex-row align-items-center flex-wrap">
          <div className='me-4 my-2'>
            <PrimaryButton
              variant="contained"
              onClick={openLicenseDialog}>
              <Icon className="me-1" fontSize="small">add</Icon>{t('standard.assignLicenseRights')}
            </PrimaryButton>
          </div>
          <div className='me-4 my-2'>
            <PrimaryButton
              variant="outlined"
              onClick={openDomainDialog}>
              {t('standard.automaticLicenseApproval')}
            </PrimaryButton>
          </div>
          <div className='me-3 me-sm-0 my-2'>
            <PrimaryButton
              variant="outlined"
              onClick={openCopyLicenseAccessDialog}>
              {t('standard.copyLicenseRights')}
            </PrimaryButton>
          </div>
          <Divider orientation="vertical" variant="middle" flexItem className="mx-4 d-none d-sm-block" />
          <div className='me-4 my-2'>
            <PrimaryButton
              variant="outlined"
              onClick={openExternalAccessDialog}>
              {t('standard.externalLicenseRights')}
            </PrimaryButton>
          </div>
        </div>
        <div className="px-2">
          <WebkuaTable
            headers={CreateHeaders([
              textHeader("userName", t('standard.email'), HeaderSizeEnum.MEDIUM, { summary: userSummary }),
              textHeader("userPersonName", t('standard.person'), HeaderSizeEnum.MEDIUM),
              textHeader("loanId", t('standard.loanId'), HeaderSizeEnum.SMALL),
              dateAndTimeHeader("loanCreatedTime", t('standard.created')),
              dateAndTimeHeader("loanReturnedTime", t('standard.returned')),
              booleanHeader("isDevicedChanged", t('customerProductListGridRenderer.moved')),
              booleanHeader("isBorrowed", t('standard.borrowed')),
              booleanHeader("isVirtualMachine", "VM"),
              booleanHeader("isNewLicensing", t('standard.newLicensingSystem')),
              textHeader("clientUserName", t('standard.user'), HeaderSizeEnum.SMALL),
              textHeader("domainName", t('standard.domain'), HeaderSizeEnum.SMALL),
              textHeader("machineName", t('standard.machine'), HeaderSizeEnum.SMALL),
              textHeader("osVersion", "OS", HeaderSizeEnum.SMALL),

              textHeader("productVersion", t("standard.productVersion"), HeaderSizeEnum.SMALL),
              textHeader("version", t("standard.version"), HeaderSizeEnum.SMALL),
              textHeader("productVersion", t('customerProductListGridRenderer.productVersion'), HeaderSizeEnum.SMALL)
            ])}
            data={licenseLoan}
          />
        </div>
      </>}
    </Box>
    {!!licenseAccessOpen &&
      <LicenseDialog
        open={!!licenseAccessOpen}
        licenseNumber={props.data.licenseNumber}
        onClose={closeLicenseDialog}
        initialPersonsOnLicense={licenseAccesses}
        maxSeats={props.data.numberOfSeats}
        isNamedUser={props.data.itemType === LicenseTypeEnum.NAMED_USER}
        lastLoanDatesMap={lastLoanDatesMap}
      />}
    {!!domain && <DomainDialog
      open={!!domain}
      onClose={closeDomainDialog}
      initialDomains={props.data.approvedDomains}
      licenseNumber={props.data.licenseNumber}
    />}
    {!!copyLicenseAccess && <CopyLicenseAccessDialog
      open={!!copyLicenseAccess}
      companyNumber={companyNumber}
      onClose={closeCopyLicenseAccessDialog}
      currentLicenseNumber={props.data.licenseNumber}
    />}
    {!!externalAccess && companyNumber &&
      <ExternalAccessDialog
        open={!!externalAccess}
        onClose={closeExternalAccessDialog}
        initialPersonsOnLicense={licenseAccesses}
        initialSelectedCompanyNumber={companyNumber}
        licenseNumber={props.data.licenseNumber}
        onSelectedCompanyChange={handleSelectedCompanyNumber}
      />}
  </>;
}

export default CustomerProductListGridRenderer;